/* ---------------------------------------------------------------------------
   An edited programme, not yet proposed.

   The Scheduler's Gantt can be dragged, resized and relinked (see
   src/data/draftSchedule.ts). What that produces is a DRAFT over the baseline
   in force — never the baseline itself, which stays frozen and goes on being
   what every variance figure is measured against. The draft becomes a plan
   only the way any revision does: proposed through the Take / Revise form,
   then accepted by an approver (LC-40).

   Kept in this browser's storage, keyed by the baseline it was drawn over, so
   a reload does not lose an afternoon's rescheduling and a draft over a
   superseded baseline is simply never read again. No vendored import here, so
   the store can ask whether there is a draft without pulling in the Gantt.
   --------------------------------------------------------------------------- */

import type { BaselineLink } from './variance.ts';

/** Every leaf's inputs, as the draft solves from them. */
export interface DraftInputs {
  baselineId: string;
  /** Working days. */
  durations: Record<string, number>;
  /** Start no earlier than, as a day, or null where the logic alone drives it. */
  starts: Record<string, string | null>;
  links: BaselineLink[];
}

const PREFIX = 'bridge-monitor/draft/';

export function readDraft(baselineId: string): DraftInputs | null {
  try {
    const raw = localStorage.getItem(PREFIX + baselineId);
    if (raw === null) return null;
    const d = JSON.parse(raw) as DraftInputs;
    if (d?.baselineId !== baselineId || typeof d.durations !== 'object' || typeof d.starts !== 'object' || !Array.isArray(d.links)) {
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

export function writeDraft(d: DraftInputs): void {
  try {
    localStorage.setItem(PREFIX + d.baselineId, JSON.stringify(d));
  } catch {
    /* Storage full or blocked: the edits stay on screen for this visit. */
  }
}

const cleared = new Set<(baselineId: string) => void>();

/** Drop the draft over a baseline — discarded, or proposed and so held by the register now. */
export function clearDraft(baselineId: string): void {
  try {
    localStorage.removeItem(PREFIX + baselineId);
  } catch {
    /* Nothing to do: a draft that cannot be removed could not have been written. */
  }
  for (const cb of cleared) cb(baselineId);
}

/** Told when a draft is cleared, so a Gantt drawing it can go back to the baseline. */
export function onDraftCleared(cb: (baselineId: string) => void): void {
  cleared.add(cb);
}
